import WarningIcon from '@mui/icons-material/Warning';
import { Alert, Stack } from '@mui/material';
import { useIntl } from 'react-intl';

import { useFeatureFlagSplit } from '@verantos-dev/app-state-base';

import { AnalyticalVariablesSection } from './AnalyticalVariablesSection';
import { SourceCohortsSection } from './SourceCohortsSection';
import { StudyPeriodSection } from './StudyPeriodSection';
import { useAnalyticalDatasetDefinitionTab } from '../../hooks/useAnalyticalDatasetDefinitionTab';

export const AnalyticalDatasetDefinitionTab = () => {
  const { formatMessage } = useIntl();
  // TODO: cleanup with asset_edit_while_in_use ff — checked once here (the
  // top-level tab component) and threaded down as a plain boolean so the
  // orchestration hook doesn't need its own flag check.
  const isAssetEditWhileInUseEnabled = Boolean(useFeatureFlagSplit('asset_edit_while_in_use'));
  const {
    isLoading,
    showStagedChangesWarning,
    showStaleAnalyticalVariablesWarning,
    staleWarningReasons = [],
  } = useAnalyticalDatasetDefinitionTab({ isAssetEditWhileInUseEnabled });

  // Picks copy that names what actually went stale — a source cohort being
  // edited+saved is a distinct case from an AV changing, and both can fire
  // at once.
  const hasStaleAv = staleWarningReasons.includes('av_changed');
  const hasStaleCohort = staleWarningReasons.includes('cohort_changed');
  const staleWarningMessageId =
    hasStaleAv && hasStaleCohort
      ? 'analyticalDatasetDetail.definition.staleAnalyticalVariablesAndCohortsWarning'
      : hasStaleCohort
      ? 'analyticalDatasetDetail.definition.staleSourceCohortsWarning'
      : 'analyticalDatasetDetail.definition.staleAnalyticalVariablesWarning';

  // Figma: 24px (spacing-6) between the stacked section cards.
  return (
    <Stack spacing={6} data-testid="ad-definition-tab">
      {showStagedChangesWarning && (
        <Alert
          severity="warning"
          icon={<WarningIcon fontSize="inherit" />}
          sx={{ py: 0.5 }}
          data-testid="ad-definition-staged-changes-warning"
        >
          {formatMessage({ id: 'analyticalDatasetDetail.definition.stagedChangesWarning' })}
        </Alert>
      )}
      {showStaleAnalyticalVariablesWarning && (
        <Alert
          severity="warning"
          icon={<WarningIcon fontSize="inherit" />}
          sx={{ py: 0.5 }}
          data-testid="ad-definition-stale-av-warning"
        >
          {formatMessage({ id: staleWarningMessageId })}
        </Alert>
      )}
      <SourceCohortsSection isLoading={isLoading} />
      <StudyPeriodSection isLoading={isLoading} />
      <AnalyticalVariablesSection isLoading={isLoading} />
    </Stack>
  );
};
